import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, message, Space, Alert } from 'antd';
import { LockOutlined, MailOutlined, SafetyOutlined } from '@ant-design/icons';
import { apiClient } from '../../utils/api';
import styled from 'styled-components';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
};

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.bgPrimary};
  padding: 24px;
`;

const StyledCard = styled(Card)`
  max-width: 500px;
  width: 100%;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border} !important;
    background: ${COLORS.bgSecondary} !important;
  }
  
  .ant-card-head-title {
    color: ${COLORS.textPrimary} !important;
    text-align: center;
  }
  
  .ant-card-body {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-input {
    background: ${COLORS.bgPrimary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
    
    &:hover, &:focus {
      border-color: ${COLORS.accent} !important;
    }
  }
  
  .ant-btn-primary {
    background: ${COLORS.accent} !important;
    border-color: ${COLORS.accent} !important;
    
    &:hover {
      background: ${COLORS.accent}dd !important;
      border-color: ${COLORS.accent}dd !important;
    }
  }
`;

const { Title, Text } = Typography;

const LogoContainer = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const Logo = styled.img`
  width: 80px;
  height: 80px;
  margin-bottom: 16px;
  transition: transform 0.2s ease;
`;

const CompleteRegistration: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userType = searchParams.get('userType');
  const registrationToken = searchParams.get('registrationToken');
  const email = searchParams.get('email');

  useEffect(() => {
    if (!userType || !registrationToken || !email) {
      setError('Invalid registration link. Please check your email for the correct link.');
    }
  }, [userType, registrationToken, email]);

  const handleSubmit = async (values: { password: string; confirmPassword: string; verificationToken: string }) => {
    if (!userType || !registrationToken || !email) {
      message.error('Invalid registration link');
      return;
    }

    if (values.password !== values.confirmPassword) {
      message.error('Passwords do not match');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const endpoint = userType === 'Administrator' 
        ? '/api/Public/Administrator/auth/complete-registration'
        : '/api/Public/Merchant/auth/complete-registration';

      await apiClient.post(endpoint, {
        email,
        password: values.password,
        verificationToken: values.verificationToken,
        registrationToken,
      });

      message.success('Registration completed successfully! Redirecting to login...');
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate(`/?userType=${userType}`);
      }, 2000);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to complete registration';
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!userType || !registrationToken || !email) {
    return (
      <PageContainer>
        <StyledCard>
          <Alert
            message="Invalid Registration Link"
            description="The registration link is invalid or missing required parameters. Please check your email for the correct link."
            type="error"
            showIcon
          />
        </StyledCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <StyledCard>
        <LogoContainer>
          <Logo src="/icon.svg" alt="Switch Wallet Logo" />
          <Title level={3} style={{ color: COLORS.textPrimary, margin: 0 }}>
            Complete Your Registration
          </Title>
        </LogoContainer>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text type="secondary" style={{ color: COLORS.textSecondary }}>
              Welcome! Please enter your verification token and set a password to complete your {userType} account registration.
            </Text>
            <div style={{ marginTop: 8 }}>
              <Text strong style={{ color: COLORS.textPrimary }}>
                <MailOutlined /> {email}
              </Text>
            </div>
          </div>

          {error && (
            <Alert
              message="Registration Failed"
              description={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
            />
          )}

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark={false}
          >
            <Form.Item
              name="verificationToken"
              label="Verification Token"
              rules={[
                { required: true, message: 'Please enter your verification token' },
                { min: 6, message: 'Verification token must be at least 6 characters' },
              ]}
              help="Enter the verification token sent to your email"
            >
              <Input
                prefix={<SafetyOutlined />}
                placeholder="Enter verification token"
                size="large"
                maxLength={10}
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Please enter your password' },
                { min: 8, message: 'Password must be at least 8 characters' },
                {
                  pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                  message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number',
                },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Enter your password"
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="Confirm Password"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm your password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Confirm your password"
                size="large"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
              >
                Complete Registration
              </Button>
            </Form.Item>
          </Form>
        </Space>
      </StyledCard>
    </PageContainer>
  );
};

export default CompleteRegistration;

